import { CodeBook } from "@king-ict/eupisi-ui/models";
import { axiosBaseQuery } from "@king-ict/eupisi-ui/utils";
import { createApi } from "@reduxjs/toolkit/query/react";

import { GenderEnum, InstitutionCodeBook, SchoolYear, SchoolYearCodeBook } from "../models";
import { MsgUtils } from "../utils";

export const codeBookApi = createApi({
  baseQuery: axiosBaseQuery(),
  endpoints: (build) => ({
    //COMMON
    getSchoolYears: build.query<SchoolYearCodeBook[], void>({
      query: () => "school-years",
    }),
    getSchoolYearList: build.query<SchoolYear[], void>({
      query: () => "school-years",
    }),
    getSchoolYearCurrent: build.query<SchoolYear, void>({
      query: () => "school-years/current",
    }),
    getLanguages: build.query<CodeBook[], void>({
      query: () => "languages",
    }),
    //GEOGRAPHY
    getCountries: build.query<CodeBook[], void>({
      query: () => "geography/countries",
    }),
    getCounties: build.query<CodeBook[], void>({
      query: () => "geography/counties",
    }),
    getCountyListForUser: build.query<CodeBook[], void>({
      query: () => "geography/counties-user",
    }),
    //PERSON
    getGenderList: build.query<CodeBook[], void>({
      query: () => "person/genders",
    }),
    getAlternativeCodeReasonList: build.query<CodeBook[], void>({
      query: () => "person/alternative-code-reasons",
    }),
    getAlternativeCodeTypeList: build.query<CodeBook[], void>({
      query: () => "person/alternative-code-types",
    }),
    getCitizenshipList: build.query<CodeBook[], void>({
      query: () => "person/citizenships",
    }),
    getNationalityList: build.query<CodeBook[], void>({
      query: () => `person/nationalities`,
    }),
    getNationalMinorityList: build.query<CodeBook[], void>({
      query: () => "person/national-minorities",
    }),
    getParentNumberList: build.query<CodeBook[], void>({
      query: () => "person/parentnumbers",
    }),
    getParentshipTypeList: build.query<CodeBook[], void>({
      query: () => "person/parentship-types",
    }),
    getProfessionalQualificationList: build.query<CodeBook[], void>({
      query: () => "person/professional-qualifications",
    }),
    getContactMethodList: build.query<CodeBook[], void>({
      query: () => "person/contact-methods",
    }),
    //SCHOOL CANDIDATE
    getSchoolCandidateAddReasonList: build.query<CodeBook[], void>({
      query: () => "school-candidates/add-reasons",
    }),
    getSchoolCandidateCancelReasonList: build.query<CodeBook[], void>({
      query: () => "school-candidates/cancel-reasons",
    }),
    getSchoolCandidateStatusList: build.query<CodeBook[], void>({
      query: () => "school-candidates/statuses",
    }),
    //REQUEST
    getDifficultyTypeList: build.query<InstitutionCodeBook[], void>({
      query: () => "request/get-difficulty-types",
    }),
    //INSTITUTIONS
    getInstitutionList: build.query<InstitutionCodeBook[], void>({
      query: () => "institutions/institutions-codebook-list",
    }),
    getInstitutionListForUser: build.query<InstitutionCodeBook[], void>({
      query: () => "institutions/institutions-codebook-list-user",
    }),
    getInstitutionShifts: build.query<CodeBook[], void>({
      query: () => "institutions/shifts",
    }),
    getPrograms: build.query<CodeBook[], void>({
      query: () => "programs/all",
    }),
    getElectiveSubjects: build.query<CodeBook[], boolean>({
      query: (forPrivate: boolean) => `subjects/elective/${forPrivate}`,
    }),
    //COMMITTEE
    getCommitteeStatusList: build.query<CodeBook[], void>({
      query: () => "committees/statuses-all",
    }),
    //FAQ
    getFaqGroupList: build.query<InstitutionCodeBook[], void>({
      query: () => "faq/group",
    }),
  }),
});

export const {
  useGetSchoolYearsQuery,
  useGetSchoolYearListQuery,
  useGetSchoolYearCurrentQuery,
  useGetCountriesQuery,
  /** Gets all counties */
  useGetCountiesQuery,
  /**
   * Gets counties for the current role 
   * - Global users have all
   * - County users have one or more
   * - Institution users have one or more
  */
  useGetCountyListForUserQuery,
  useGetLanguagesQuery,
  /**
   * Gets the Gender list using an API call.
   */
  useGetGenderListQuery,
  useGetAlternativeCodeReasonListQuery,
  useGetAlternativeCodeTypeListQuery,
  useGetCitizenshipListQuery,
  useGetNationalityListQuery,
  useGetNationalMinorityListQuery,
  useGetParentNumberListQuery,
  useGetParentshipTypeListQuery,
  useGetProfessionalQualificationListQuery,
  useGetContactMethodListQuery,
  useGetSchoolCandidateAddReasonListQuery,
  useGetSchoolCandidateCancelReasonListQuery,
  useGetSchoolCandidateStatusListQuery,
  useGetElectiveSubjectsQuery,
  useGetDifficultyTypeListQuery,
  useGetInstitutionShiftsQuery,
  useGetProgramsQuery,
  useGetInstitutionListQuery,
  useGetInstitutionListForUserQuery,
  useGetCommitteeStatusListQuery,
  useGetFaqGroupListQuery,
} = codeBookApi;

/**
 * Gets Genders using the enum without an API call.
 * @returns 
 */
export const useGetGenderList = (): { data: CodeBook[] } => {
  var codebooks = MsgUtils.getEnumToCodeBook(GenderEnum, "gender");
  return { data: codebooks };
}